import React, { FC, useEffect, useState } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Box, Button, FormControl, FormControlLabel, Grid, Link, Radio, RadioGroup, TextField } from '@mui/material';
import { EEOccupationalLevel, WorkForceGoalAndTargetRow } from '../../../../Component/Model/Enum/EmploymentEquity';
import { LocalizationProvider } from '@mui/x-date-pickers';
import WorkforceProfileOccupationalLevelDataGrid from './WorkforceProfileOccupationalLevelGrid';
import { useRecoilValue } from 'recoil';
import { ScorecardAtom } from '../../../../State/Scorecard.Atom';
import { DataImportType, UserActivityActionType } from '../../../../Component/Model/Enum/BEE123Enums';
import { CoreExportService } from '../../../../Component/Api/Export/CoreExportService';
import { toast } from 'react-toastify';
import { Export } from '../../../../Component/Model/Dto/Export';
import { MDBox } from '../../../../Component/Common/Components/MDBox';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { MDAlert } from '../../../../Component/Common/Components/MDAlert';
import { StaticDataService } from '../../../../Component/Api/StaticData/StaticDataService';
import { EEScorecardAtom } from '../../../../State/EEScorecard.Atom';
import { getProvinceLookup } from '../../../Common/StaticData/Province';
import { WorkforceProfileGoalsAndTargetsService } from '../../../../Component/Api/EmploymentEquity/WorkforceProfileGoalsAndTargets/WorkforceProfileGoalsAndTargetsService';
import { WorkforceProfileGoalsAndTargets } from '../../../../Component/Model/Dto/EmploymentEquity/WorkforceProfileGoalsAndTargets/WorkforceProfileGoalsAndTargets';
import { AppliedSectoralTargetsModal } from './AppliedTargets/AppliedSectoralTargetsModal';
import { AllEAPTargetsModal } from './AppliedTargets/AllEAPTargetsModal';
import { getDateWithoutTime } from '../../../../Helpers/DateFormatter';
import { UserActivityService } from '../../../../Component/Api/UserActivity/UserActivityService';
import { GoalsAndTargetsSection } from '../../../../Component/Common/Constants/UserActivityTrackingSections';
import { ValidatedMobileDatePicker } from '../../../../Component/Common/Components/ValidatedMobileDatePicker/ValidatedMobileDatePicker';

interface IGoalsProps {
	disabled: boolean;
	workforceProfileDate: Date;
	onDateChange: (date: Date) => void;
}

export const GoalsAndTargetsNew: FC<IGoalsProps> = (props) => {
	const [showPercentage, setShowPercentage] = React.useState(true);
	const [appliedSectoralPercentageName, setAppliedSectoralPercentageName] = React.useState<string>('');
	const [appliedEAPPercentageName, setAppliedEAPPercentageName] = React.useState<string>('');
	const [commentData, setCommentData] = useState<WorkforceProfileGoalsAndTargets.CommentsDtoOut[]>([]);
	const scorecard = useRecoilValue(ScorecardAtom);
	const eeScorecard = useRecoilValue(EEScorecardAtom);
	const [summaryTarget, setSummaryTarget] = useState<WorkForceGoalAndTargetRow>();
	const [showSectoralTargetsModal, setShowSectoralTargetsModal] = useState<boolean>(false);
	const [showAllEAPTargetsModal, setShowAllEAPTargetsModal] = useState<boolean>(false);

	const exportWorkforceProfile = async () => {
		const request: Export.FileExportDto = {
			DataImportType: DataImportType.EEPlanWorkforceProfile,
			parameters: [
				{ key: 'Date', value: props.workforceProfileDate },
				{ key: 'IsDisabled', value: props.disabled }
			],
			scorecardId: scorecard.scorecardId
		};
		try {
			let response = await CoreExportService.ExportFile(request);
			const redirectUrl = response.data;
			window.open(redirectUrl, '_blank');
		} catch (error) {
			console.log(error);
			toast.error('Export could not be completed');
		}
	};

	const getAppliedSectoralPercentage = async () => {
		try {
			const sectoralResponse = await StaticDataService.getEAPSectoralSectors();
			setAppliedSectoralPercentageName(
				sectoralResponse.data?.find((data) => data.Id === eeScorecard.AppliedSectoralPercentage)?.Name ?? ''
			);
		} catch (error) {
			console.log(error);
		}
	};

	const getAppliedEapOptions = () => {
		const appliedEapAndSectoralOptions = getProvinceLookup();
		setAppliedEAPPercentageName(
			appliedEapAndSectoralOptions.find((data) => data.id === eeScorecard.AppliedEapPercentage)?.value ?? ''
		);
	};

	const occupationalLevels: EEOccupationalLevel[] = [
		EEOccupationalLevel.Top_Management,
		EEOccupationalLevel.Senior_Management,
		EEOccupationalLevel.Middle_Management,
		EEOccupationalLevel.Junior_Management,
		EEOccupationalLevel.Semi_Skilled,
		EEOccupationalLevel.Unskilled,
		EEOccupationalLevel.Temporary
	];

	const formatTitle = (level: EEOccupationalLevel): string => {
		return EEOccupationalLevel[level].replace(/_/g, ' ');
	};

	useEffect(() => {
		getAppliedEapOptions();
		getAppliedSectoralPercentage();
		getComments();
	}, [props.workforceProfileDate]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		UserActivityService.createActivityLog({
			section: GoalsAndTargetsSection,
			actionType: UserActivityActionType.View,
			actionDescription: "View workforce profile numerical goals and targets",
			scorecardId: scorecard.scorecardId,
			requestUrl: window.location.href
		});
	}, []);

	const handleMetricsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setShowPercentage(event.target.value === 'Headcount & Percentage');
	};

	const getComments = async () => {
		const response = await WorkforceProfileGoalsAndTargetsService.GetComments(scorecard.scorecardId, props.disabled);
		setCommentData(response.data);
	};

	const handleTargetRowChange = (yearId?: WorkForceGoalAndTargetRow) => {
		setSummaryTarget(yearId);
	};

	return (
		<>
			<Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<ValidatedMobileDatePicker
						label="Select Workforce Profile Actual Date"
						inputFormat="yyyy/MM/dd"
						mask="____/__/__"
						value={props.workforceProfileDate}
						onChange={(date: Date) => {
							if (date) {
								props.onDateChange(getDateWithoutTime(date));
							}
						}}
						renderInput={(params: any) => <TextField {...params} sx={{ width: 300 }} />}
						views={['year', 'month', 'day']}
					/>
				</LocalizationProvider>

				<FormControl component="fieldset">
					<RadioGroup
						row
						name="metrics"
						value={showPercentage ? 'Headcount & Percentage' : 'Headcount Only'}
						onChange={handleMetricsChange}
					>
						<FormControlLabel label="Headcount Only" value="Headcount Only" control={<Radio />} />
						<FormControlLabel label="Headcount & Percentage" value="Headcount & Percentage" control={<Radio />} />
					</RadioGroup>
				</FormControl>
			</Box>
			{appliedSectoralPercentageName != '' && appliedEAPPercentageName != '' && (
				<Grid container mt={1.5} mb={1.5}>
					<Grid item xs={12} xl={12} sx={{ lineHeight: '0.7' }}>
						<MDTypography component="span" variant="body2" fontWeight="bold" fontSize="0.7em">
							Applied Sectoral:&nbsp;
						</MDTypography>
						<Link
							onClick={() => {
								setShowSectoralTargetsModal(true);
							}}
							style={{ cursor: 'pointer' }}
						>
							<MDTypography
								component="span"
								variant="body2"
								fontSize="0.7em"
								style={{ color: '#097384', textDecoration: 'underline' }}
							>
								{appliedSectoralPercentageName}
							</MDTypography>
						</Link>
					</Grid>
					<Grid item xs={12} xl={12} sx={{ lineHeight: '0.7' }}>
						<MDTypography component="span" variant="body2" fontWeight="bold" fontSize="0.7em">
							Applied EAP:&nbsp;
						</MDTypography>
						<Link
							onClick={() => {
								setShowAllEAPTargetsModal(true);
							}}
							style={{ cursor: 'pointer' }}
						>
							<MDTypography 
								component="span" 
								variant="body2" 
								fontSize="0.7em" 
								style={{ color: '#097384', textDecoration: 'underline' }}
							>
								{appliedEAPPercentageName}
							</MDTypography>
						</Link>
					</Grid>
				</Grid>
			)}

			{props.disabled && (
				<WorkforceProfileOccupationalLevelDataGrid
					key={EEOccupationalLevel.Unspecified}
					showPercentage={showPercentage}
					occupationalLevel={EEOccupationalLevel.Unspecified}
					title={'Designated Disabled Employees Summary'}
					scorecardId={scorecard.scorecardId}
					isDisabled={props.disabled}
					date={getDateWithoutTime(props.workforceProfileDate)}
					commentData={commentData}
					showSummary={true}
					onYearTargetChange={handleTargetRowChange}
					refetchSummaryTarget={summaryTarget}
				/>
			)}

			{occupationalLevels.map((level) => (
				<WorkforceProfileOccupationalLevelDataGrid
					key={level}
					showPercentage={showPercentage}
					occupationalLevel={level}
					title={formatTitle(level)}
					scorecardId={scorecard.scorecardId}
					isDisabled={props.disabled}
					date={getDateWithoutTime(props.workforceProfileDate)}
					commentData={commentData}
					showSummary={false}
					onYearTargetChange={handleTargetRowChange}
				/>
			))}

			<Grid container>
				<Grid item xs={12} lg={12}>
					<MDBox mt={4}>
						<MDAlert color="light">
							<MDTypography component="span" variant="body" fontSize="0.93rem" fontWeight="regular">
								<p style={{ marginBottom: '0.2rem' }}>
									Click
									<Button
										variant="text"
										onClick={exportWorkforceProfile}
										size="small"
										style={{
											fontSize: 'inherit',
											padding: '0.25rem',
											paddingBottom: '0.4rem',
											minWidth: '0',
											textTransform: 'none'
										}}
									>
										here
									</Button>
									to Export data to a file
								</p>
							</MDTypography>
						</MDAlert>
					</MDBox>
				</Grid>
			</Grid>

			{showSectoralTargetsModal && (
				<AppliedSectoralTargetsModal
					scorecardId={scorecard.scorecardId}
					appliedSectorName={appliedSectoralPercentageName}
					eePlanYearStart={new Date(eeScorecard.EEPlanDateFrom).getFullYear().toString()}
					eePlanYearEnd={new Date(eeScorecard.EEPlanDateTo).getFullYear().toString()}
					handleCancel={() => setShowSectoralTargetsModal(false)}
				/>
			)}

			{showAllEAPTargetsModal && (
				<AllEAPTargetsModal
					scorecardId={scorecard.scorecardId}
					appliedSectorName={appliedEAPPercentageName}
					handleCancel={() => setShowAllEAPTargetsModal(false)}
				/>
			)}
		</>
	);
};
